import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, Sparkles } from "lucide-react";
import { API, api, extractError } from "@/lib/api";

const fmtDT = (iso) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });

// Saved AI summary for [start, end]; auto-generates (streaming) when missing.
export function AISummary({ start, end }) {
    const [state, setState] = useState({ loading: true });
    const [text, setText] = useState("");
    const ctrlRef = useRef(null);

    const generate = useCallback(async () => {
        ctrlRef.current?.abort();
        const ctrl = new AbortController();
        ctrlRef.current = ctrl;
        setText("");
        setState((s) => ({ ...s, streaming: true, error: null }));
        try {
            const res = await fetch(`${API}/timemachine/summary/stream?start=${start}&end=${end}`, { method: "POST", credentials: "include", signal: ctrl.signal });
            if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || `Errore ${res.status}`); }
            const reader = res.body.getReader();
            const dec = new TextDecoder();
            let buf = "";
            for (;;) {
                const { value, done } = await reader.read();
                if (done) break;
                buf += dec.decode(value, { stream: true });
                const parts = buf.split("\n\n");
                buf = parts.pop();
                for (const p of parts) {
                    if (!p.startsWith("data: ")) continue;
                    const msg = JSON.parse(p.slice(6));
                    if (msg.t) setText((x) => x + msg.t);
                    if (msg.error) throw new Error(msg.error);
                    if (msg.done) setState({ ai: true, generated_at: msg.generated_at, model: msg.model });
                }
            }
            setState((s) => (s.streaming ? { ...s, streaming: false, error: "La generazione si è interrotta" } : s));
        } catch (e) {
            if (!ctrl.signal.aborted) setState((s) => ({ ...s, streaming: false, error: e.message }));
        }
    }, [start, end]);

    useEffect(() => {
        let alive = true;
        setState({ loading: true });
        setText("");
        api.get("/timemachine/summary", { params: { start, end } }).then(({ data }) => {
            if (!alive) return;
            if (data.summary) { setText(data.summary.content); setState({ ai: data.ai_available, generated_at: data.summary.generated_at, model: data.summary.model }); }
            else if (data.ai_available) { setState({ ai: true }); generate(); }
            else setState({ ai: false });
        }).catch((e) => alive && setState({ error: extractError(e) }));
        return () => { alive = false; ctrlRef.current?.abort(); };
    }, [start, end, generate]);

    return (
        <div className="bg-white border border-neutral-200 rounded-md p-6" data-testid="tm-ai-summary">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold inline-flex items-center gap-1.5"><Sparkles size={12} /> Resoconto</div>
                {state.ai && !state.streaming && (
                    <button onClick={generate} data-testid="tm-ai-regenerate" className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1">
                        <RefreshCw size={12} /> Rigenera
                    </button>
                )}
            </div>
            {state.loading && <div className="text-sm text-neutral-400">Caricamento…</div>}
            {state.ai === false && !text && <div className="text-sm text-neutral-500" data-testid="tm-ai-unavailable">Resoconto AI non disponibile: nessuna chiave Anthropic configurata in Impostazioni → Integrazioni. Sotto trovi comunque tutti i dati raccolti.</div>}
            {state.streaming && !text && <div className="text-sm text-neutral-400 animate-pulse" data-testid="tm-ai-generating">Sto scrivendo il resoconto…</div>}
            {text && <div className="text-[15px] leading-relaxed text-neutral-800 whitespace-pre-wrap" data-testid="tm-ai-text">{text}</div>}
            {state.error && <div className="text-sm text-red-600 mt-2" data-testid="tm-ai-error">Errore nella generazione: {state.error}</div>}
            {state.generated_at && !state.streaming && <div className="text-xs text-neutral-400 mt-4" data-testid="tm-ai-generated-at">Generato il {fmtDT(state.generated_at)}{state.model ? ` · ${state.model}` : ""}</div>}
        </div>
    );
}
